import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as Yup from 'yup';
import { trackEvent } from 'helpers/analytics';
import { useGetPublicSignupFormQuery, useSubmitPublicSignupFormMutation } from 'services/signupForms';
import { useGetNetworkQuery } from 'services/organizationsV2';
import { linkedinProfileRegex } from 'helpers/regExs';
import { transformApiFieldsToInternal } from '../listDetail/signupForm/utils/transformers';

const generateInitialValues = (fields) => {
  const values = {};
  fields?.forEach((field) => {
    const fieldId = field.id?.toString() || field.tempId;
    if (!fieldId) return;

    switch (field.type) {
      case 'checkbox':
        values[fieldId] = field.allowMultiple ? [] : false;
        break;
      case 'select':
        values[fieldId] = field.allowMultiple ? [] : '';
        break;
      default:
        values[fieldId] = '';
    }
  });
  return values;
};

const generateValidationSchema = (fields) => {
  const shape = {};

  fields?.forEach((field) => {
    const fieldId = field.id?.toString() || field.tempId;
    if (!fieldId) return;

    let validator;

    switch (field.type) {
      case 'email':
        validator = Yup.string().email('Please enter a valid email address');
        if (field.required) {
          validator = validator.required(`${field.label} is required`);
        }
        break;
      case 'url':
        if (field.label?.toLowerCase().includes('linkedin')) {
          validator = Yup.string().test(
            'linkedin-url',
            'Your profile URL should look like http://linkedin.com/in/your-user-name',
            (value) => !value || linkedinProfileRegex.test(value),
          );
        } else {
          validator = Yup.string().url('Please enter a valid URL');
        }
        if (field.required) {
          validator = validator.required(`${field.label} is required`);
        }
        break;
      case 'checkbox':
        if (field.allowMultiple) {
          validator = Yup.array();
          if (field.required) {
            validator = validator.min(1, `${field.label} is required`);
          }
        } else {
          validator = Yup.boolean();
          if (field.required) {
            validator = validator.oneOf([true], `${field.label} is required`);
          }
        }
        break;
      case 'select':
        if (field.allowMultiple) {
          validator = Yup.array();
          if (field.required) {
            validator = validator.min(1, `${field.label} is required`);
          }
        } else {
          validator = Yup.string();
          if (field.required) {
            validator = validator.required(`${field.label} is required`);
          }
        }
        break;
      default:
        validator = Yup.string();
        if (field.required) {
          validator = validator.required(`${field.label} is required`);
        }
    }

    shape[fieldId] = validator;
  });

  shape.termsAccepted = Yup.boolean().oneOf(
    [true],
    'Please accept our terms of service and privacy policy to continue.',
  );

  return Yup.object().shape(shape);
};

export const usePublicSignupForm = ({ slug, networkId }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const hasTrackedView = useRef(false);

  const { data: networkData, isLoading: isNetworkLoading } = useGetNetworkQuery(networkId, {
    skip: !networkId,
  });

  const {
    data: rawFormData,
    isLoading: isFormLoading,
    isError,
    error: fetchError,
  } = useGetPublicSignupFormQuery({ slug, networkId }, { skip: !slug || !networkId });

  const isLoading = isFormLoading || isNetworkLoading;

  const formData = useMemo(() => {
    if (!rawFormData) return null;
    return {
      ...rawFormData,
      fields: transformApiFieldsToInternal(rawFormData.signupFormFields),
    };
  }, [rawFormData]);

  const [submitForm, { isLoading: isSubmitting }] = useSubmitPublicSignupFormMutation();

  const initialValues = useMemo(() => {
    const fieldValues = formData?.fields ? generateInitialValues(formData.fields) : {};
    return {
      ...fieldValues,
      termsAccepted: false,
    };
  }, [formData?.fields]);

  const validationSchema = useMemo(() => generateValidationSchema(formData?.fields), [formData?.fields]);

  const onSubmit = useCallback(
    async (values) => {
      setSubmitError(null);

      try {
        const submissionData = {};
        formData?.fields?.forEach((field) => {
          const fieldId = field.id?.toString() || field.tempId;
          if (!fieldId) return;

          const value = values[fieldId];

          // Skip empty values
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return;
          }

          submissionData[field.label] = value;
        });

        await submitForm({
          slug,
          networkId,
          _rawData: { submissionData },
        }).unwrap();

        trackEvent('contacts:list:signup_form_submitted', {
          network_id: networkId,
          slug,
          list_id: formData?.sharedListId,
        });
        setIsSubmitted(true);
      } catch (err) {
        setSubmitError(err?.data?.message || 'Something went wrong. Please try again.');
      }
    },
    [formData, networkId, slug, submitForm],
  );

  const isNotFound = !isLoading && (isError || !formData || !formData.active);

  // Track form viewed event once when form is successfully loaded
  useEffect(() => {
    if (formData && !isLoading && !isNotFound && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent('contacts:list:signup_form_viewed', {
        network_id: networkId,
        slug,
        form_id: formData.id,
        shared_list_id: formData.sharedListId,
      });
    }
  }, [formData, isLoading, isNotFound, networkId, slug]);

  return {
    formData,
    networkName: networkData?.name || formData?.sharedList?.collection?.name || formData?.networkName,
    networkLogo: networkData?.logoUrl || formData?.sharedList?.collection?.logoUrl || formData?.networkLogo,
    isLoading,
    isSubmitting,
    isSubmitted,
    isNotFound,
    fetchError,
    submitError,
    // Formik config
    initialValues,
    validationSchema,
    onSubmit,
  };
};
